import React from "react";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import "../styles/Cart.css";
import offersData from "../data/offersData";

const Cart = () => {
  const { cartItems, increaseQuantity, decreaseQuantity, totalPrice } = useCart();
  const phoneNumber = offersData.contact.phoneNumber;
  const navigate = useNavigate();

  const createWhatsAppMessage = () => {
    if (cartItems.length === 0) return "";

    let message = `Hello! 👋 I want to place an order for the following products:%0A%0A`;

    cartItems.forEach(({ id, name, quantity }, index) => {
      message += `${index + 1}. ${name}%0A   Quantity: ${quantity}%0A   Product ID: ${id}%0A%0A`;
    });

    message += `💰 *Total Amount: ₹${totalPrice.toFixed(2)}*%0A%0A`;
    message += `Please confirm the order and provide delivery details. Thank you! 🙏`;

    return message;
  };

  const whatsappLink = `https://wa.me/${phoneNumber}?text=${createWhatsAppMessage()}`;

  const handleQuantityDecrease = (id, currentQuantity, name) => {
    decreaseQuantity(id);
    // Optional: Add toast notification for better UX
    if (currentQuantity === 1) {
      console.log(`${name} removed from cart`);
    }
  };

  const handleQuantityIncrease = (id, name) => {
    increaseQuantity(id);
    // Optional: Add toast notification for better UX
    console.log(`${name} quantity increased`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <main className="cart-page" role="region" aria-label="Shopping cart">
      <h2>Your Cart</h2>

      {cartItems.length === 0 ? (
        <section className="empty-cart" aria-live="polite">
          <p>Your cart is empty! 🛒</p>
          <p>
            Looks like you haven't added any delicious products yet. 
            Browse our amazing selection and start filling your cart with goodies!
          </p>
          <button
            type="button"
            onClick={() => navigate("/products")}
            className="browse-products-button"
            aria-label="Navigate to products page to start shopping"
          >
            🛍️ Browse Products
          </button>
        </section>
      ) : (
        <>
          <div className="cart-content">
            <ul className="cart-items-list" role="list" aria-label="Cart items">
              {cartItems.map(({ id, name, price, offerPrice, quantity }) => {
                const itemPrice = offerPrice && offerPrice < price ? offerPrice : price;
                const itemTotal = itemPrice * quantity;

                return (
                  <li key={id} className="cart-item" role="listitem">
                    <div className="item-info">
                      <h3 className="item-name" title={name}>
                        {name}
                      </h3>
                      <div className="item-price" aria-label={`Unit price: ${formatCurrency(itemPrice)}`}>
                        {formatCurrency(itemPrice)}
                        {offerPrice && offerPrice < price && (
                          <span className="original-price" aria-label={`Original price: ${formatCurrency(price)}`}>
                            {formatCurrency(price)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div 
                      className="quantity-controls" 
                      role="group" 
                      aria-label={`Quantity controls for ${name}`}
                    >
                      <button
                        onClick={() => handleQuantityDecrease(id, quantity, name)}
                        aria-label={quantity === 1 ? `Remove ${name} from cart` : `Decrease quantity of ${name}`}
                        type="button"
                        className="quantity-btn decrease"
                        title={quantity === 1 ? "Remove from cart" : "Decrease quantity"}
                      >
                        {quantity === 1 ? "🗑️" : "−"}
                      </button>
                      <span 
                        className="quantity-display"
                        aria-live="polite" 
                        aria-atomic="true" 
                        aria-label={`Current quantity: ${quantity}`}
                      >
                        {quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityIncrease(id, name)}
                        aria-label={`Increase quantity of ${name}`}
                        type="button"
                        className="quantity-btn increase"
                        title="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <div
                      className="item-total"
                      aria-label={`Total price for ${name}: ${formatCurrency(itemTotal)}`}
                    >
                      {formatCurrency(itemTotal)}
                    </div>
                  </li>
                );
              })}
            </ul>

            <section className="cart-summary" aria-live="polite" aria-atomic="true">
              <h2 aria-label={`Cart total: ${formatCurrency(totalPrice)}`}>
                Total: {formatCurrency(totalPrice)}
              </h2>
              <p className="item-count" aria-label={`${cartItems.length} items in cart`}>
                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
              </p>
            </section>

            <div className="checkout-section">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-order-button"
                aria-label={`Order ${cartItems.length} items worth ${formatCurrency(totalPrice)} via WhatsApp`}
                role="button"
              >
                Order via WhatsApp
              </a>
              <p className="order-info">
                📞 You'll be redirected to WhatsApp with your order details
              </p>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default Cart;